import type { ComponentContentSchema } from '@vibesites/runtime';
import { RichTextContent, useTemplateContext } from '@vibesites/runtime/react';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';

// Hyperlocal page_copy may arrive as either pre-formatted HTML (from generated
// hyperlocal sources) or plain text. We treat anything containing an HTML tag
// as already-formatted and pass it through; plain strings get wrapped in <p>
// with HTML special chars escaped so user content can't poison the markup.
function normalizePageCopy(raw: string | undefined | null, fallback: string): string {
  if (!raw) return fallback;
  const text = String(raw);
  if (/<[a-z][\s\S]*>/i.test(text)) return text;
  const escaped = text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/\n{2,}/g, '</p><p>')
    .replace(/\n/g, '<br />');
  return `<p>${escaped}</p>`;
}

export function HyperlocalPageCopy() {
  useRevealStyles();
  const sectionRef = useScrollReveal<HTMLElement>();

  const ctx = useTemplateContext();
  const hl = ctx?.hyperlocal as Record<string, string | null | undefined> | undefined;

  const copyDefault = normalizePageCopy(hl?.page_copy, '<p>Page content will be displayed here.</p>');

  return (
    <section ref={sectionRef} className="reveal-fade-up py-16 md:py-24 bg-[var(--vibe-color-background)]">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <RichTextContent
          path="hyperlocalpagecopy.text"
          label="Page Copy"
          defaultValue={copyDefault}
          as="div"
          className="font-body text-base md:text-lg leading-relaxed text-[var(--vibe-color-text)] [&_p]:mb-4 [&_h2]:font-heading [&_h2]:text-xl [&_h2]:sm:text-2xl [&_h2]:font-normal [&_h2]:text-[var(--vibe-color-text)] [&_h2]:mb-4 [&_h2]:mt-8 [&_h3]:font-heading [&_h3]:text-lg [&_h3]:font-normal [&_h3]:text-[var(--vibe-color-text)] [&_h3]:mb-3 [&_h3]:mt-6 [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:mb-4 [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:mb-4 [&_li]:mb-1 [&_a]:text-primary [&_a]:underline"
        />
      </div>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'HyperlocalPageCopy',
  displayName: 'Hyperlocal Page Copy',
  tokens: {
    'hyperlocalpagecopy.text': {
      label: 'Page Copy',
      type: 'richtext',
      defaultValue: '<p>Page content will be displayed here.</p>',
    },
  },
};

(HyperlocalPageCopy as any).contentSchema = contentSchema;

export default HyperlocalPageCopy;
